import { CurrencyPipe } from '@angular/common';
import { AnalyticsData, AnalyticsTest } from './analytics.interface';

const currencyPipe = new CurrencyPipe('en-US');

const toNumber = (value: string): number => Number(value);
const toNumberThreeDecimals = (value: string): string => !value ? '-' :
  Number(value).toFixed(3);
const toPercentThreeDecimals = (value: string): string => !value ? '-' :
  `${Number(value).toFixed(3)}%`;

const formatScenariosBasedOnMetric = (
  value: string,
  _: any,
  metric: string
) => {
  if (metric === 'What you might get back after costs') {
    return currencyPipe.transform(value, '', '', '1.2-2');
  } else {
    return toPercentThreeDecimals(value);
  }
};

const summaryOrderedTests = new Map<string, Function>([
  ['Summary Risk Indicator', toNumber],
  ['Market Risk Measure', toNumber],
  ['Credit Risk Measure', toNumber],
  [
    'Initial Investment',
    (value: string, data: AnalyticsData) => {
      const currency = AnalyticsDataHelper.getCurrencyFromData(data);
      return `${currencyPipe.transform(
        Number(value),
        '',
        '',
        '1.0'
      )} ${currency}`;
    },
  ],
  ['Price', (value: string) => `${Number(value).toFixed(2)}%`],
  ['Fair Value', toNumberThreeDecimals],
  ['Tenor', (value: string) => value],
  ['Net Present Value', toNumberThreeDecimals],
  ['Value-at-risk', toNumberThreeDecimals],
  ['VaR-Equivalent Volatility', toNumberThreeDecimals],
  ['Early Term', toNumberThreeDecimals],
]);

const costsOrderedTests = new Map([
  ['Total Costs', (value) => Number(value).toFixed(2)],
  ['Annual Cost Impact - %', toPercentThreeDecimals],
  ['Avge Return at RHP After Cost - %', toPercentThreeDecimals],
  ['Avge Return at RHP Before Cost - %', toPercentThreeDecimals],
  ['Summary Cost Indicator', toNumberThreeDecimals],
]);

const returnScenariosOrderedTests = new Map<string, Function>([
  ['Stress scenario', formatScenariosBasedOnMetric],
  ['Unfavourable scenario', formatScenariosBasedOnMetric],
  ['Moderate scenario', formatScenariosBasedOnMetric],
  ['Favourable scenario', formatScenariosBasedOnMetric],
]);

export class AnalyticsDataHelper {
  private static sortData(
    tests: AnalyticsTest[],
    type: 'summary' | 'costs' | 'returnScenarios'
  ): AnalyticsTest[] {
    const keys = Array.from(AnalyticsDataHelper.getDatasetByType(type).keys());
    const property =
      type === 'returnScenarios' ? 'testDescription' : 'metricDescription';

    return tests.sort((a, b) => {
      if (a[property] === b[property]) {
        return a.metricDescription.localeCompare(b.metricDescription) ? -1 : 1;
      }

      const orderOfA = keys.indexOf(
        keys.filter((key) => a[property].startsWith(key))?.[0]
      );
      const orderOfB = keys.indexOf(
        keys.filter((key) => b[property].startsWith(key))?.[0]
      );

      return orderOfA - orderOfB;
    });
  }

  static formatDataResults(
    data: AnalyticsData,
    type: 'summary' | 'costs' | 'returnScenarios'
  ): AnalyticsTest[] {
    return AnalyticsDataHelper.sortData(data.tests, type).map((test) => {
      const format = AnalyticsDataHelper.getDatasetByType(type).get(
        test[
          type === 'returnScenarios' ? 'testDescription' : 'metricDescription'
        ]
      );
      if (format) {
        test = {
          ...test,
          metricValue: format(test.metricValue, data, test.metricDescription),
        };
      }
      return test;
    });
  }

  static getDatasetByType(type: string): Map<string, Function> {
    return type === 'summary'
      ? summaryOrderedTests
      : type === 'costs'
      ? costsOrderedTests
      : returnScenariosOrderedTests;
  }

  static getCurrencyFromData(data: AnalyticsData): string {
    return data?.productCaptureDetails?.summary?.currency ?? '';
  }
}
